import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Utensils, AlertTriangle, ArrowRight, Sparkles } from 'lucide-react';
import api from '../services/api';

/**
 * NutritionTargetsPanel — Nutrition-page slice of the unified daily flow.
 * Shows today's kcal/protein/meals against the user's BASELINE TARGETS
 * (not a vague "hunger" feeling). Mirrors the contextual pattern used by
 * Body/Hunger/Mind panels: tells the user what's happening + what to do.
 *
 * Empty state surfaces a CTA to "set my targets" — opens the chat with the
 * `nutrition_baseline` intent so the form renders inline.
 */

const Bar = ({ value, target, color, label, sublabel, testId }) => {
  const pct = Math.max(0, Math.min(150, value / Math.max(1, target) * 100));
  // Sweet spot 90-110% — anything else is off
  const inBand = pct >= 90 && pct <= 110;
  const tone = inBand ? color : pct < 90 ? '#fb923c' : '#f87171';
  return (
    <div className="flex-1 min-w-0" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary">{label}</p>
        <p className="text-xs text-zinc-300"><span className="font-secondary font-bold" style={{ color: tone }}>{value}</span><span className="text-zinc-600"> / {target}</span></p>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: tone, transition: 'width 700ms cubic-bezier(.2,.8,.2,1)' }} />
      </div>
      <p className="text-[10px] text-zinc-500 mt-1">{sublabel}</p>
    </div>
  );
};

const NutritionTargetsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.getNutritionProfile();
      setData(r.data || null);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('wellness:refresh', h);
    return () => window.removeEventListener('wellness:refresh', h);
  }, [load]);

  if (loading) return null;

  const status = data?.status;
  const targets = data?.targets;
  const adh = data?.adherence_today;

  // ── No baseline yet → CTA to chat onboarding ──
  if (!status?.is_set || !targets) {
    return (
      <div className="hpme-card p-5" data-testid="nutrition-targets-empty">
        <div className="hpme-card-corner" />
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4" style={{ color: '#fb923c' }} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-secondary font-bold" style={{ color: '#fb923c' }}>SET YOUR NUTRITION BASELINE</span>
        </div>
        <p className="text-xs text-zinc-300 leading-relaxed mb-3">
          I don't have your stats yet — without them I can't tell you if you're hitting the right calorie or protein target. Share a few numbers and Nikki will compute realistic daily goals.
        </p>
        <Link
          to="/chat?intent=nutrition_baseline"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-black text-xs font-secondary font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90"
          data-testid="nutrition-baseline-cta"
        >
          Talk to Nikki <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  // ── Baseline set, zero meals today → soft prompt ──
  if (!adh || adh.meals_logged === 0) {
    return (
      <div className="hpme-card p-5" data-testid="nutrition-targets-no-meals">
        <div className="hpme-card-corner" />
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Utensils className="w-4 h-4" style={{ color: '#fb923c' }} />
            <span className="text-[10px] uppercase tracking-[0.2em] font-secondary font-bold" style={{ color: '#fb923c' }}>TODAY VS TARGETS</span>
          </div>
          <Link to="/chat?intent=nutrition_baseline" className="text-[10px] text-zinc-500 hover:text-primary uppercase tracking-wider" data-testid="nutrition-edit-baseline">
            Edit baseline
          </Link>
        </div>
        <p className="text-xs text-zinc-300 leading-relaxed mb-2 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" />
          No meals logged yet today.
        </p>
        <p className="text-[11px] text-zinc-500">
          Today's targets: <span className="text-zinc-300">{adh.kcal_target} kcal · {adh.protein_target_g}g protein · {adh.meal_target} meals</span>. Log your first meal below and this panel comes alive.
        </p>
      </div>
    );
  }

  // ── Populated: real progress bars vs real targets ──
  return (
    <div className="hpme-card p-5" data-testid="nutrition-baseline-panel">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Utensils className="w-4 h-4" style={{ color: '#fb923c' }} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-secondary font-bold" style={{ color: '#fb923c' }}>TODAY VS TARGETS</span>
        </div>
        <Link to="/chat?intent=nutrition_baseline" className="text-[10px] text-zinc-500 hover:text-primary uppercase tracking-wider" data-testid="nutrition-edit-baseline">
          Edit baseline
        </Link>
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <Bar value={adh.kcal} target={adh.kcal_target} color="#fb923c" label="CALORIES" sublabel={`${adh.kcal_pct}% of target`} testId="nutrition-bar-kcal" />
        <Bar value={adh.protein_g} target={adh.protein_target_g} color="#a855f7" label="PROTEIN g" sublabel={`${adh.protein_pct}% of target`} testId="nutrition-bar-protein" />
        <Bar value={adh.meals_logged} target={adh.meal_target} color="#CCFF00" label="MEALS" sublabel={`${adh.meals_logged} of ${adh.meal_target} planned`} testId="nutrition-bar-meals" />
      </div>
    </div>
  );
};

export default NutritionTargetsPanel;
